import { yupResolver } from '@hookform/resolvers'
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog'
import { ItemVoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomItemTextField'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import {
  amtNumStr,
  amtStr,
} from '@ifca-root/react-component/src/helpers/StringNumberFunction/numFormatter'
import {
  CircularProgress,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { calculateDocAmt } from 'containers/ARAPModule/Helper/AmountCalculation'
import {
  CommonStatus,
  useGetClassificationCodeQuery,
  useGetDepartmentListingByItemIdLazyQuery,
  useGetGlCompanyPolicyQuery,
  useGetTaxSchemeNewListingQuery,
  useGetTaxExemptionCalculationLazyQuery,
} from 'generated/graphql'
import { calculateTaxDetail } from 'helpers/Calculation/taxCalculationDetail'
import { filterTaxSchemeEffectiveDate } from 'helpers/Filter/filterTaxSchemeEffectiveDate'
import { CommonYupValidation } from 'helpers/Form/YupValidation'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import * as yup from 'yup'
import { DefaultDepartmentHandlerService } from 'containers/GeneralLedgerModule/Hooks/DefaultDepartment/DefaultDepartmentService'
import { TaxExemptionCalculationData } from 'containers/APModule/components/ItemDialog'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'

interface ARDebitNoteItemProps {
  BillItemID: string
  CostCentreID: string
  Amount: number
  TaxSchemeID: string
  TaxAmt: any
  TaxAmtDetail: any
  TaxRate: number
  DocAmt: number
  Remark: string
  einvoice_classification_code_id: string
  TaxExemptionReason: string
  TaxExemptionAmount: number
  TaxExemptionRate: number
}

export const ARDebitNoteItemDialog = (props: any) => {
  const {
    menu,
    watch,
    CompanyID,
    getBillItem,
    openItemDialog,
    BillItemLoading,
    taxEffectiveDate,
    setOpenItemDialog,
    debitNoteItemData,
  } = props

  const [showExemptedFields, setShowExemptedFields] = useState<boolean>(
    !!menu?.obj?.TaxExemptionReason
  )

  const DebitNoteItemFormSchema = yup.object().shape({
    BillItemID: CommonYupValidation.requireField('Bill Item is required'),
    CostCentreID: CommonYupValidation.requireField('Department is required'),
    Amount: CommonYupValidation.requireField('Amount is required'),
    Remark: CommonYupValidation.requireField('Remark is required'),
    ...(!!showExemptedFields
      ? {
          TaxExemptionReason: CommonYupValidation.requireField(
            SystemMsgs.taxExemptionReason()
          ),
        }
      : null),
  })

  /* -------------------------------------------- */
  /*                  USESTATE                    */
  /* -------------------------------------------- */
  const [selectedTax, setSelectedTax] = useState(null)
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [taxRate, setTaxRate] = useState(0.0)
  const [eInvoiceItemName, setEInvoiceItemName] = useState(null)
  const [itemRecord, setItemRecord] = useState(false)
  const [itemVoiceRemark, setItemVoiceRemark] = useState('')
  const [taxAmt, setTaxAmt] = useState(!!menu?.obj ? menu?.obj?.TaxAmt : 0)
  const [cost, setCost] = useState<number>(0)

  const [classificationCode, setClassificationCode] = useState(
    !!menu?.obj ? menu?.obj?.einvoice_classification_code_id : null
  )

  const [mountDefVal, setMountDefVal] = useState(!!menu?.obj)
  const [useDefaultDept, setUseDefaultDept] = useState(false)

  const [exemptionVoiceReason, setExemptionVoiceReason] = useState('')
  const [exemptionRecord, setExemptionRecord] = useState(false)

  /* -------------------------------------------- */
  /*                  USE FORM                    */
  /* -------------------------------------------- */
  const {
    handleSubmit: handleSubmitItem,
    register: registerItem,
    errors: errorsItem,
    control: controlItem,
    getValues: getValuesItem,
    watch: watchItem,
    setValue: setValueItem,
    clearErrors: clearErrorsItem,
  } = useForm<ARDebitNoteItemProps>({
    mode: 'onSubmit',
    resolver: yupResolver(DebitNoteItemFormSchema),
  })

  /* -------------------------------------------- */
  /*                   QUERIES                    */
  /* -------------------------------------------- */
  const { loading: GLCompanyLoading } = useGetGlCompanyPolicyQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: error => {
      let errorMessage = error.message.substring(15)
      console.log('ERROR', error)
      setErrorDia(true)
      setErrMsg(errorMessage)
      setValueItem('UOMID', null)
    },
    onCompleted: ({ getGLCompanyPolicy }) => {
      setUseDefaultDept(getGLCompanyPolicy[0]?.DefaultDepartmentBalanceSheetCOA)
    },
  })

  const {
    loading: TaxSchemeOutputLoading,
    data: { getTaxSchemeListing } = { getTaxSchemeListing: [] },
  } = useGetTaxSchemeNewListingQuery({
    fetchPolicy: 'network-only',
    variables: { CommonStatus: CommonStatus.Active, TaxClass: 'OUTPUT' },
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      if (menu?.obj?.TaxSchemeID) {
        let filteredTaxScheme = filterTaxSchemeEffectiveDate(
          taxEffectiveDate,
          getTaxSchemeListing
        )

        let selectTax = filteredTaxScheme.find(
          x => x?.id === menu?.obj?.TaxSchemeID
        )
        setSelectedTax(selectTax)
        setValueItem('TaxSchemeID', selectTax?.id)
        clearErrorsItem('TaxSchemeID')

        initializeTaxDetailsInEditMode(selectTax)

        let recalculateCost =
          selectTax?.isExclusive === true
            ? menu?.obj?.DocAmt - menu?.obj?.TaxAmt
            : menu?.obj?.DocAmt

        setCost(recalculateCost)
        setValueItem('Amount', recalculateCost)
      }

      // Fetch department listing
      if (menu?.obj?.BillItemID && mountDefVal === true) {
        fetchDepartmentListingByItemID({
          variables: { CompanyID, BillItemID: menu?.obj?.BillItemID },
        })
      }
    },
  })

  const {
    loading: ClassificationCodeLoading,
    data: { getClassificationCode } = { getClassificationCode: [] },
  } = useGetClassificationCodeQuery({
    fetchPolicy: 'network-only',
    onCompleted: ({ getClassificationCode }) => {
      if (watch('is_einvoice') === true) {
        setClassificationCode(
          !!menu?.obj?.einvoice_classification_code_id
            ? menu?.obj?.einvoice_classification_code_id
            : null
        )
        if (menu?.obj?.einvoice_classification_code_id != null) {
          setEInvoiceItemName(
            getClassificationCode?.filter(
              x => x?.id === menu?.obj?.einvoice_classification_code_id
            )[0].classificationCode +
              ` (` +
              getClassificationCode?.filter(
                x => x?.id === menu?.obj?.einvoice_classification_code_id
              )[0].description +
              `)`
          )
        }
      }
    },
  })

  /* -------------------------------------------- */
  /*                   DEF DEPT                   */
  /* -------------------------------------------- */
  const [
    fetchDepartmentListingByItemID,
    {
      loading: HandlerLoading,
      called: HandlerCalled,
      data: { getDepartmentListingByItemID } = {
        getDepartmentListingByItemID: [],
      },
    },
  ] = useGetDepartmentListingByItemIdLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: ({ getDepartmentListingByItemID }) => {
      DefaultDepartmentHandlerService.InitializeDepartmentValueInEditMode({
        DepartmentListingArray: getDepartmentListingByItemID,
        DefaultDepartmentPolicy: useDefaultDept, // nullable; used for GL Department Policy checking
        EditData: menu?.obj,
        SetValue: setValueItem,
        Register: registerItem,
        MountDefVal: mountDefVal, // to avoid overriding costcentreid on another api call
      })
    },
    onError: error => {
      let errorMessage = error.message.substring(15)
      console.log('ERROR', error)
      setErrorDia(true)
      setErrMsg(`${errorMessage}"`)
    },
  })

  const isDefaultChecking: boolean =
    HandlerCalled === true
      ? !!(
          getDepartmentListingByItemID?.length === 1 &&
          !!getDepartmentListingByItemID[0]?.IsDefault
        )
      : false

  const disableDeptController: boolean = !!(
    useDefaultDept === true && isDefaultChecking === true
  )
  //============================================================================

  /* -------------------------------------------- */
  /*                 EXEMPTED TAX                 */
  /* -------------------------------------------- */
  const [awaitCalc, setAwaitCalc] = useState<boolean>(false)
  const [debounceTimer, setDebounceTimer] = useState(null)
  const [taxExemptionData, setTaxExemptionData] = useState<
    TaxExemptionCalculationData
  >({
    TaxExemptionRate: menu?.obj?.TaxExemptionRate ?? null,
    TaxExemptionAmount: menu?.obj?.TaxExemptionAmount ?? null,
  })

  const [
    fetchExemptedTaxCalc,
    { loading: ExemptedTaxCalcLoading },
  ] = useGetTaxExemptionCalculationLazyQuery({
    fetchPolicy: 'network-only',
    onError: error => {
      let errorMessage = error.message.substring(15)
      console.log('ERROR', error)
      setErrorDia(true)
      setErrMsg(`${errorMessage}"`)

      setAwaitCalc(false)
    },
    onCompleted: ({ getTaxExemptionCalculation }) => {
      setTaxExemptionData({
        TaxExemptionRate: getTaxExemptionCalculation?.TaxExemptionRate,
        TaxExemptionAmount: getTaxExemptionCalculation?.TaxExemptionAmount,
      })

      setAwaitCalc(false)
    },
  })

  // Debounce method for API based calculation
  const debounceExemptCalc = (
    currTaxSchemeId: string,
    effDate: Date,
    baseAmt: number
  ) => {
    setAwaitCalc(true)
    setTaxExemptionData({ TaxExemptionAmount: null, TaxExemptionRate: null })
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    const newTimer = setTimeout(() => {
      fetchExemptedTaxCalc({
        variables: {
          TaxSchemeID: currTaxSchemeId,
          EffectiveDate: effDate,
          BaseAmount: baseAmt,
        },
      })
    }, 1500)

    setDebounceTimer(newTimer)
  }

  //============================================================================

  const taxSchemeOutput = filterTaxSchemeEffectiveDate(
    taxEffectiveDate,
    getTaxSchemeListing
  )

  const taxSchemeName = taxSchemeOutput?.filter(
    tax => tax?.id === selectedTax?.id
  )[0]?.name

  const costCentreCode =
    watchItem('CostCentreID') !== undefined
      ? getDepartmentListingByItemID?.find(
          x => x?.CostCentreID === watchItem('CostCentreID')
        )?.Code
      : getDepartmentListingByItemID?.find(
          x => x?.CostCentreID === menu?.obj?.CostCentreID
        )?.Code

  const billItemName =
    watchItem('BillItemID') !== undefined
      ? getBillItem.filter(x => x?.BillItemID === watchItem('BillItemID'))[0]
          ?.Name
      : getBillItem.filter(x => x?.BillItemID === menu?.obj?.BillItemID)[0]
          ?.Name

  /* -------------------------------------------- */
  /*             ONCREATE & ONUPDATE              */
  /* -------------------------------------------- */
  const onCreateDebitNoteItem = (data, docAmt) => {
    const taxIsExclusive = getTaxSchemeListing?.find(
      x => x?.id === data?.TaxSchemeID
    )?.isExclusive

    const formattedTaxDetails = result.taxDetails.map((tax, idx) => {
      return {
        seqNo: tax?.SeqNo,
        taxAmt: watchItem(`TaxAmtDetail[${idx}]`) ?? tax.TaxAmount,
        taxRate: tax.TaxRate * 100,
        taxCode: tax.taxCode,
        taxId: tax.taxId,
        taxSchemeDetailId: tax.taxSchemeDetailId,
        taxSchemeId: tax.taxSchemeId,
      }
    })

    debitNoteItemData.push({
      DebitNoteItemID: data?.DebitNoteItemID,
      BillItemName: billItemName,
      BillItemID: data?.BillItemID,
      CostCentreName: costCentreCode,
      CostCentreID: data?.CostCentreID,
      Amount:
        taxIsExclusive === true
          ? parseFloat(amtNumStr(data?.Amount))
          : docAmt - data?.TaxAmt,
      Sequence: debitNoteItemData?.length + 1,
      TaxSchemeID: data?.TaxSchemeID,
      TaxCode: taxSchemeName,
      TaxRate: 0,
      TaxAmt: data?.TaxAmt,
      DocAmt: docAmt,

      Remark: data?.Remark,
      ItemTaxInfo: formattedTaxDetails,
      einvoice_classification_code_id: classificationCode,
      TaxExemptionReason:
        showExemptedFields && data?.TaxExemptionReason
          ? data?.TaxExemptionReason
          : null,
      TaxExemptionAmount:
        showExemptedFields && taxExemptionData?.TaxExemptionAmount
          ? taxExemptionData?.TaxExemptionAmount
          : null,
      TaxExemptionRate:
        showExemptedFields && taxExemptionData?.TaxExemptionRate
          ? taxExemptionData?.TaxExemptionRate
          : null,
    })
    setOpenItemDialog(false)
  }

  const onUpdateDebitNoteItem = (data, index, docAmt) => {
    const taxIsExclusive = getTaxSchemeListing?.find(
      x => x?.id === data?.TaxSchemeID
    )?.isExclusive

    const formattedTaxDetails = result.taxDetails.map((tax, idx) => {
      return {
        seqNo: tax?.SeqNo,
        taxAmt: watchItem(`TaxAmtDetail[${idx}]`) ?? tax.TaxAmount,
        taxRate: tax.TaxRate * 100,
        taxCode: tax.taxCode,
        taxId: tax.taxId,
        taxSchemeDetailId: tax.taxSchemeDetailId,
        taxSchemeId: tax.taxSchemeId,
      }
    })

    debitNoteItemData[index] = {
      DebitNoteItemID: data.DebitNoteItemID,
      BillItemName: billItemName,
      BillItemID: data.BillItemID,
      CostCentreName: costCentreCode,
      CostCentreID: data.CostCentreID,
      Amount:
        taxIsExclusive === true
          ? parseFloat(amtNumStr(data?.Amount))
          : docAmt - data?.TaxAmt,
      Sequence: data?.Sequence,
      TaxCode: taxSchemeName,
      TaxRate: data?.TaxRate || 0,
      TaxSchemeID:
        data?.TaxSchemeID ||
        getBillItem?.filter(x => x?.TaxSchemeID === data?.TaxSchemeID)[0]
          ?.TaxSchemeID,
      TaxAmt: data.TaxAmt,
      DocAmt: docAmt,

      Remark: data.Remark,
      ItemTaxInfo: formattedTaxDetails,
      einvoice_classification_code_id: classificationCode,
      TaxExemptionReason:
        showExemptedFields && data?.TaxExemptionReason
          ? data?.TaxExemptionReason
          : null,
      TaxExemptionAmount:
        showExemptedFields && taxExemptionData?.TaxExemptionAmount
          ? taxExemptionData?.TaxExemptionAmount
          : null,
      TaxExemptionRate:
        showExemptedFields && taxExemptionData?.TaxExemptionRate
          ? taxExemptionData?.TaxExemptionRate
          : null,
    }
    setOpenItemDialog(false)
  }

  /* -------------------------------------------- */
  /*                  FUNCTION                    */
  /* -------------------------------------------- */
  // ✥✥✥✥✥✥✥✥✥✥✥✥✥✥ - TAX CALCULATION - ✥✥✥✥✥✥✥✥✥✥✥✥✥✥ //
  const docAmt = selectedTax?.isExclusive
    ? calculateDocAmt(cost, taxAmt)
    : Number(parseFloat(String(watchItem('Amount')).replace(/,/g, '')) || 0)

  const prepareTaxCalculationData = (selectedTax: any) => {
    if (!selectedTax?.taxSchemeDetailList[0]?.taxSchemeDetailItemList?.length) {
      return []
    }

    const taxSchemeDetail = selectedTax?.taxSchemeDetailList[0]

    return taxSchemeDetail.taxSchemeDetailItemList.map(item => ({
      taxId: item.taxId,
      taxSchemeId: taxSchemeDetail.taxSchemeId,
      taxSchemeDetailId: taxSchemeDetail.id,
      seqNo: item.seqNo,
      taxOnSeq: item.taxOnSeq || [],
      computeMethod:
        item.computeMethod === 'TAX_ON_GROSS'
          ? 'Tax_On_Gross'
          : 'Tax_On_Taxed_Price',
      taxRate: item.taxRate,
      taxCode: item.taxInfo?.code,
    }))
  }

  const calculateTaxFromState = (selectedTax: any, amount: number) => {
    const taxData = prepareTaxCalculationData(selectedTax)

    const isExclusive = Boolean(selectedTax?.isExclusive)

    return calculateTaxDetail(taxData, amount, isExclusive)
  }
  let result = calculateTaxFromState(selectedTax, cost)

  const initializeTaxDetailsInEditMode = selectTax => {
    if (menu?.obj?.TaxAmtDetails && Array.isArray(menu?.obj?.TaxAmtDetails)) {
      menu.obj.TaxAmtDetails.forEach((taxDetail, index) => {
        registerItem(`TaxAmtDetail[${index}]`)
        setValueItem(`TaxAmtDetail[${index}]`, taxDetail.taxAmount)
      })
      setValueItem('taxDetailsCount', menu.obj.TaxAmtDetails.length)
    } else if (selectTax) {
      const currentAmount = Number(
        selectTax?.isExclusive === true ? menu?.obj?.Amount : menu?.obj?.DocAmt
      )
      const taxResult = calculateTaxFromState(selectTax, currentAmount)
      if (taxResult?.taxDetails) {
        updateTaxDetailsInForm(taxResult.taxDetails, taxResult.TaxAmount)
      }
    }
  }

  const updateTaxDetailsInForm = (taxDetails, taxAmount) => {
    if (!taxDetails || taxDetails.length === 0) return
    const prevTaxDetailsCount = watchItem('taxDetailsCount') || 0
    taxDetails.forEach((tax, index) => {
      registerItem(`TaxAmtDetail[${index}]`)
      setValueItem(`TaxAmtDetail[${index}]`, tax.TaxAmount)
    })
    setValueItem('taxDetailsCount', taxDetails.length)
    if (prevTaxDetailsCount > taxDetails.length) {
      for (let i = taxDetails.length; i < prevTaxDetailsCount; i++) {
        setValueItem(`TaxAmtDetail[${i}]`, null)
      }
    }
    setValueItem('TaxAmt', taxAmount)
    setTaxAmt(taxAmount)
  }

  const handleBillItemChange = BillItemID => {
    setValueItem('BillItemID', BillItemID)
    let defTax = getBillItem?.filter(x => x?.BillItemID === BillItemID)[0]
      ?.TaxSchemeID
    let selectedTax = taxSchemeOutput.find(x => x?.id === defTax)
    setValueItem('TaxSchemeID', selectedTax?.id)
    setSelectedTax(selectedTax)
    clearErrorsItem('BillItemID')
    const taxResult = calculateTaxFromState(selectedTax, cost)
    setTaxAmt(taxResult?.TaxAmount)
    setValueItem('TaxAmt', taxResult?.TaxAmount)
    updateTaxDetailsInForm(taxResult?.taxDetails, taxResult?.TaxAmount)

    // when changing item, fetch direct lazy query
    if (selectedTax?.isExemptedTaxScheme === true) {
      setShowExemptedFields(true)
      fetchExemptedTaxCalc({
        variables: {
          TaxSchemeID: defTax,
          EffectiveDate: new Date(taxEffectiveDate),
          BaseAmount: Number(cost),
        },
      })
    }
  }

  const handleAmtChange = event => {
    setCost(event.value)
    if (selectedTax) {
      prepareTaxCalculationData(selectedTax)
      const taxResult = calculateTaxFromState(selectedTax, event.value)
      setTaxAmt(taxResult?.TaxAmount)
      updateTaxDetailsInForm(taxResult?.taxDetails, taxResult?.TaxAmount)
      setValueItem(
        'TaxAmt',
        calculateTaxFromState(selectedTax, event.value)?.TaxAmount
      )

      if (selectedTax?.isExemptedTaxScheme === true) {
        debounceExemptCalc(
          selectedTax?.id,
          new Date(taxEffectiveDate),
          Number(event.value)
        )
      }
    }
  }

  const handleTaxChange = event => {
    const currentTax = taxSchemeOutput.find(x => x?.id === event?.target?.value)
    const taxResult = calculateTaxFromState(currentTax, cost)
    updateTaxDetailsInForm(taxResult?.taxDetails, taxResult?.TaxAmount)
    setSelectedTax(taxSchemeOutput.find(x => x?.id === event?.target?.value))
    setValueItem('TaxSchemeID', event?.target?.value)
    setTaxAmt(taxResult?.TaxAmount)

    // Exempted Tax portion
    setShowExemptedFields(!!currentTax?.isExemptedTaxScheme)

    if (currentTax?.isExemptedTaxScheme === true) {
      fetchExemptedTaxCalc({
        variables: {
          TaxSchemeID: event?.target?.value,
          EffectiveDate: new Date(taxEffectiveDate),
          BaseAmount: Number(cost),
        },
      })
    }
  }

  const handleTaxAmtChange = event => {
    setTaxAmt(event.value)
  }

  const checkClassificationCode = billItem => {
    if (
      billItem?.einvoice_classification_code_id !== null &&
      billItem?.einvoice_classification_code_id !== undefined &&
      billItem?.einvoice_classification_code_id !== ''
    )
      return true
    else return false
  }

  /* -------------------------------------------- */
  /*                  USEEFFECT                   */
  /* -------------------------------------------- */
  useEffect(() => {
    if (!isNaN(taxAmt)) {
      setValueItem('TaxAmt', taxAmt)
      clearErrorsItem('TaxAmt')
    }
  }, [taxAmt])

  return (
    <>
      {HandlerLoading && <Loading />}
      {BillItemLoading && <Loading />}
      {GLCompanyLoading && <Loading />}
      {ExemptedTaxCalcLoading && <Loading />}
      {TaxSchemeOutputLoading && <Loading />}
      {ClassificationCodeLoading && <Loading />}
      <CommonDialog
        fullWidth={true}
        open={openItemDialog}
        onClose={() => {
          setOpenItemDialog(false)
          setTaxAmt(null)
          setSelectedTax(null)
        }}
        sections={{
          header: {
            dynamic: (
              <div>
                <div className="dialog-dynamic-content">
                  <div className="session">
                    <div className="flex session">
                      <div
                        className="dialog-title flex-space"
                        style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: 'orange',
                        }}
                      >
                        {'Debit Note Detail'}
                      </div>
                      <div
                        className="dialog-title right-text"
                        style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: 'orange',
                        }}
                      >
                        {menu?.obj ? 'Edit' : 'New'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="infoline-content" />
              </div>
            ),
          },
          body: () => (
            <>
              <div className="content-container" style={{ marginTop: '-20px' }}>
                <Controller
                  render={({ onChange, onBlur, value }) => {
                    const defVal = getBillItem?.filter(
                      x => x?.BillItemID === menu?.obj?.BillItemID
                    )[0]

                    return (
                      <Autocomplete
                        options={
                          watch('is_einvoice') === true
                            ? getBillItem.filter(
                                item => item.einv_integrated === true
                              )
                            : getBillItem?.sort((a, b) => {
                                return a.Name.localeCompare(b.Name)
                              }) || []
                        }
                        getOptionLabel={option =>
                          `${option?.Name} | ${option?.Description}`
                        }
                        fullWidth
                        onChange={(value, newValue: any, reason) => {
                          handleBillItemChange(newValue?.BillItemID)
                          setValueItem('BillItemID', newValue?.BillItemID)
                          setValueItem('CostCentreID', null)
                          fetchDepartmentListingByItemID({
                            variables: {
                              CompanyID,
                              BillItemID: newValue?.BillItemID,
                            },
                          })
                          if (checkClassificationCode(newValue) === true) {
                            setClassificationCode(
                              newValue?.einvoice_classification_code_id
                            )
                            if (
                              newValue?.einvoice_classification_code_id !== null
                            ) {
                              setEInvoiceItemName(
                                getClassificationCode?.filter(
                                  x =>
                                    x?.id ===
                                    newValue?.einvoice_classification_code_id
                                )[0].classificationCode +
                                  ` (` +
                                  getClassificationCode?.filter(
                                    x =>
                                      x?.id ===
                                      newValue?.einvoice_classification_code_id
                                  )[0].description +
                                  `)`
                              )
                            }
                          }
                          setMountDefVal(false)
                          setValueItem(
                            'Remark',
                            `${newValue?.Name} | ${newValue?.Description}`
                          )
                        }}
                        renderOption={(props, option) => {
                          return (
                            <div>
                              <div>
                                <span className="xsTitle">{props?.Name}</span>
                              </div>
                              <div className="desc">{props?.Description}</div>
                            </div>
                          )
                        }}
                        defaultValue={defVal}
                        renderInput={(params: any) => {
                          return (
                            <div>
                              <TextField
                                {...params}
                                required
                                label="Bill Item"
                                variant="standard"
                                fullWidth
                                name="BillItemID"
                                value={value}
                                helperText={errorsItem?.BillItemID?.message}
                                error={errorsItem?.BillItemID ? true : false}
                                defaultValue={menu?.obj?.BillItemID || ''}
                              />
                            </div>
                          )
                        }}
                      />
                    )
                  }}
                  name="BillItemID"
                  autoComplete="off"
                  control={controlItem}
                  multiline={true}
                  fullWidth
                  margin="dense"
                  ref={registerItem}
                  required
                  defaultValue={menu?.obj?.BillItemID || ''}
                  helperText={errorsItem?.BillItemID?.message}
                  error={errorsItem?.BillItemID ? true : false}
                />

                {/* Classification e-Invoice */}
                {watch('is_einvoice') === true && (
                  <Controller
                    render={({ value, onChange }) => {
                      return (
                        <TextField
                          label="Classification"
                          value={eInvoiceItemName ?? ''}
                          InputProps={{ readOnly: true }}
                          fullWidth
                          disabled
                        />
                      )
                    }}
                    margin="dense"
                    name="einvoice_classification_code_id"
                    ref={registerItem}
                    control={controlItem}
                    disabled
                  />
                )}

                <Controller
                  render={({ value, onChange }) => {
                    const defVal = !!watchItem('CostCentreID')
                      ? getDepartmentListingByItemID?.find(
                          x => x?.CostCentreID === watchItem('CostCentreID')
                        )
                      : null
                    return (
                      <Autocomplete
                        options={getDepartmentListingByItemID || []}
                        getOptionLabel={option =>
                          option
                            ? `${option?.MasterCode} | ${option?.Name}`
                            : ''
                        }
                        fullWidth
                        onChange={(value, newValue: any) => {
                          onChange(newValue?.CostCentreID)
                        }}
                        disableClearable={disableDeptController}
                        disabled={disableDeptController}
                        renderOption={(props, option) => {
                          return (
                            <div>
                              <div>
                                <span className="xsTitle">
                                  {props?.MasterCode}
                                </span>
                              </div>
                              <div className="desc">{props?.Name}</div>
                            </div>
                          )
                        }}
                        value={
                          disableDeptController === true
                            ? getDepartmentListingByItemID[0]
                            : defVal
                        }
                        renderInput={(params: any) => {
                          return (
                            <div>
                              <TextField
                                {...params}
                                helperText={
                                  errorsItem?.CostCentreID
                                    ? 'Department is required'
                                    : ''
                                }
                                error={errorsItem?.CostCentreID ? true : false}
                                label="Department"
                                style={{ width: '100%' }}
                                margin="dense"
                                disabled={disableDeptController}
                                required
                              />
                            </div>
                          )
                        }}
                      />
                    )
                  }}
                  label="Department"
                  name="CostCentreID"
                  autoComplete="off"
                  control={controlItem}
                  multiline={true}
                  fullWidth
                  margin="dense"
                  ref={registerItem}
                  helperText={errorsItem?.CostCentreID?.message}
                  error={errorsItem?.CostCentreID ? true : false}
                  disabled={disableDeptController}
                  required
                />
                <Controller
                  as={<NumberFormat allowNegative={false} />}
                  thousandSeparator
                  customInput={TextField}
                  id="standard-basic"
                  name="Amount"
                  label="Amount"
                  value={cost}
                  autoComplete="off"
                  control={controlItem}
                  onValueChange={e => {
                    handleAmtChange(e)
                  }}
                  decimalScale={2}
                  fixedDecimalScale
                  margin="dense"
                  required
                  fullWidth
                  helperText={errorsItem?.Amount?.message}
                  error={errorsItem?.Amount ? true : false}
                  ref={registerItem}
                  defaultValue={cost || ''}
                />
                <Grid
                  item
                  xs={12}
                  style={{ marginBottom: '5px', paddingRight: '10px' }}
                >
                  <Controller
                    as={
                      <TextField
                        margin="normal"
                        label="Tax Scheme"
                        required
                        SelectProps={{
                          renderValue: () =>
                            `${selectedTax?.name} | ${
                              selectedTax?.description
                            } ${selectedTax?.isExclusive ? `(E)` : `(I)`}`,
                          onChange: event => {
                            handleTaxChange(event)
                            setValueItem(
                              'TaxSchemeID',
                              event?.target?.value.toString()
                            )
                            clearErrorsItem('TaxSchemeID')
                          },
                        }}
                        select
                        helperText={errorsItem?.TaxSchemeID?.message}
                        error={errorsItem?.TaxSchemeID ? true : false}
                        fullWidth
                      >
                        <ListSubheader>Output</ListSubheader>
                        {taxSchemeOutput?.map((taxScheme, index) => (
                          <MenuItem
                            id="tax-select"
                            key={index}
                            value={taxScheme?.id}
                          >
                            {
                              <div className="content-wrap full">
                                <div>
                                  <span className="xsTitle">
                                    {taxScheme?.name}
                                  </span>
                                </div>
                                <div className="desc">
                                  {taxScheme?.description}{' '}
                                  {taxScheme?.isExclusive ? `(E)` : `(I)`}
                                </div>
                              </div>
                            }
                          </MenuItem>
                        ))}
                      </TextField>
                    }
                    select
                    name="TaxSchemeID"
                    autoComplete="off"
                    control={controlItem}
                    defaultValue={menu?.obj?.TaxSchemeID || ''}
                    ref={registerItem}
                  />
                </Grid>

                {result.taxDetails.length > 0 && (
                  <Grid item xs={12}>
                    <Paper
                      elevation={1}
                      style={{
                        padding: '1px 7px',
                        borderRadius: '2px',
                        backgroundColor: '#fff9f2',
                      }}
                    >
                      {result.taxDetails.map((tax, index) => (
                        <Grid
                          container
                          key={index}
                          spacing={1}
                          alignItems="center"
                        >
                          <Grid item xs={4}>
                            <Typography
                              variant="body2"
                              style={{
                                fontSize: '0.63rem',
                                fontWeight: 500,
                                lineHeight: '24px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              {tax?.taxCode || `Tax ${index + 1}`}
                            </Typography>
                          </Grid>

                          <Grid item xs={3}>
                            <Typography
                              variant="body2"
                              style={{
                                fontSize: '0.63rem',
                                fontWeight: 500,
                                lineHeight: '24px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              {typeof tax?.TaxRate === 'number'
                                ? `${(tax.TaxRate * 100).toFixed(2)}%`
                                : `N/A`}
                            </Typography>
                          </Grid>

                          <Grid item xs={5}>
                            <Controller
                              name={`TaxAmtDetail[${index}]`}
                              control={controlItem}
                              defaultValue={tax.TaxAmount}
                              render={({ onChange, value }) => (
                                <NumberFormat
                                  customInput={TextField}
                                  fullWidth
                                  className="disabled-input"
                                  thousandSeparator
                                  decimalScale={2}
                                  fixedDecimalScale
                                  style={{
                                    marginTop: '10px',
                                    fontSize: '0.63rem',
                                  }}
                                  allowNegative={false}
                                  size="small"
                                  value={
                                    watchItem(`TaxAmtDetail[${index}]`) ??
                                    tax.TaxAmount
                                  }
                                  InputProps={{
                                    disableUnderline: true,
                                    readOnly: true,
                                    style: {
                                      fontSize: '0.63rem',
                                      height: '23px',
                                      padding: '0 4px',
                                    },
                                  }}
                                  InputLabelProps={{
                                    style: { display: 'none' },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12} style={{ height: '16px' }} />
                <Controller
                  as={<NumberFormat allowNegative={false} />}
                  thousandSeparator
                  customInput={TextField}
                  ref={registerItem}
                  control={controlItem}
                  margin="dense"
                  name="TaxAmt"
                  label="Tax Amount"
                  onValueChange={e => {
                    handleTaxAmtChange(e)
                    setTaxAmt(e?.floatValue)
                  }}
                  value={taxAmt}
                  defaultValue={menu?.obj?.TaxAmt ?? taxAmt ?? 0}
                  decimalScale={2}
                  fixedDecimalScale
                  className="left"
                  InputProps={{ disableUnderline: true, readOnly: true }}
                />
                <Controller
                  render={({ value, onChange }) => {
                    value = { docAmt }
                    return (
                      <TextField
                        margin="dense"
                        label="Doc Amount"
                        value={amtStr(docAmt)}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        className="right"
                        disabled
                      />
                    )
                  }}
                  disabled
                  margin="dense"
                  defaultValue={(docAmt || 0) ?? (menu?.obj?.DocAmt || 0)}
                  name="DocAmt"
                  ref={registerItem}
                  control={controlItem}
                />
                <ItemVoiceTextField
                  mounted={true}
                  label="Remark"
                  name="Remark"
                  value={itemVoiceRemark}
                  setValue={setValueItem}
                  record={itemRecord}
                  setRecord={setItemRecord}
                  control={controlItem}
                  controllerProps={{
                    error: errorsItem?.Remark ? true : false,
                    defaultValue: menu?.obj?.Remark,
                    helperText: errorsItem?.Remark?.message,
                    ref: registerItem,
                    autoComplete: 'off',
                  }}
                  required
                />
                {showExemptedFields && (
                  <>
                    <ItemVoiceTextField
                      mounted={true}
                      required
                      label="Tax Exemption Reason"
                      name="TaxExemptionReason"
                      value={exemptionVoiceReason}
                      setValue={setValueItem}
                      record={exemptionRecord}
                      setRecord={setExemptionRecord}
                      control={controlItem}
                      controllerProps={{
                        error: errorsItem?.TaxExemptionReason ? true : false,
                        defaultValue: menu?.obj?.TaxExemptionReason,
                        helperText: errorsItem?.TaxExemptionReason?.message,
                        ref: registerItem,
                        autoComplete: 'off',
                      }}
                    />
                    <TextField
                      margin="dense"
                      label="Exemptible Tax Amount"
                      value={amtStr(taxExemptionData?.TaxExemptionAmount)}
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        endAdornment: (
                          <>
                            {awaitCalc ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                          </>
                        ),
                      }}
                      className="left"
                      disabled
                    />
                    {/* <TextField
                      margin="dense"
                      label="Exemptible Tax Rate"
                      value={`${amtStr(taxExemptionData?.TaxExemptionRate)}%`}
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        endAdornment: (
                          <>
                            {awaitCalc ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                          </>
                        ),
                      }}
                      className="right"
                      disabled
                    /> */}
                  </>
                )}
              </div>
            </>
          ),
          footer: {
            actions: [
              {
                displayText: 'Cancel',
                props: {
                  onClick: () => {
                    setOpenItemDialog(false)
                    setTaxAmt(null)
                    setSelectedTax(null)
                    setClassificationCode(null)
                    setEInvoiceItemName(null)
                  },
                  variant: 'contained',
                  color: 'primary',
                },
              },

              {
                displayText: 'Confirm',
                props: {
                  onClick: () => {
                    handleSubmitItem(() =>
                      menu?.obj
                        ? onUpdateDebitNoteItem(
                            watchItem(),
                            menu?.index,
                            docAmt
                          )
                        : onCreateDebitNoteItem(watchItem(), docAmt)
                    )()
                    setClassificationCode(null)
                    setEInvoiceItemName(null)
                  },
                  variant: 'contained',
                  color: 'primary',
                },
              },
            ],
          },
        }}
      />
      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error!'}
      />
    </>
  )
}
